import { HTTPClientInterface, HTTPRequest } from '@shared/core/HTTPClientInterface';
import { buildURL, SystemURL } from "./URLCatalog";

export namespace NovedadesExportServiceModel {
    export type EmployeeSelectionMode = "ALL_WITH_DATA" | "SELECTED" | "ONE";
    export type ExportRowMode = "FLAT_FIELDS" | "CONCEPT_ROWS";
    export type WorkedHoursCalculationMode =
        "ALL_EXTRA_AS_REGULAR" |
        "SHIFT_EXTRA_AS_50" |
        "SATURDAY_CUTOFF";
    export type WorkedHoursCalculationQueryMode =
        "STANDARD_CONCILIATION" |
        "SATURDAY_CUTOFF_CONCILIATION" |
        "CUSTOM_SQL";

    export interface StartProcessRequest {
        organizationID: string;
        title: string;
        dateFrom: string;
        dateTo: string;
        period?: string;
        employeeSelectionMode: EmployeeSelectionMode;
        selectedEmployeeIDs?: string[];
        exportSystem?: string;
        calculationProfileID?: string;
    }

    export interface ProcessRecordDTO extends StartProcessRequest {
        processID: string;
        status: string;
        currentStep?: string;
        includeWorkedHours?: boolean;
        employeeCount: number;
        processedEmployeeCount: number;
        failedEmployeeCount: number;
        errorsCount: number;
        warningsCount: number;
        totals?: Record<string, number>;
        outputFilePath?: string;
        outputFilename?: string;
        workedHoursCalculationProfileID?: string;
        workedHoursCalculationProfileSnapshot?: WorkedHoursCalculationProfileDTO;
        lastErrors?: ProcessLastErrorDTO[] | Record<string, ProcessLastErrorDTO>;
        lastErrorsCount?: number;
        lastErrorsUpdatedAt?: number;
        firestoreCreationTimestamp?: number;
        firestoreUpdateTimestamp?: number;
    }

    export interface ProcessLastErrorDTO {
        rowID?: string;
        rowIndex?: number;
        employeeID?: string;
        novedadAcronym?: string;
        errorType: string;
        message: string;
    }

    export interface ExportProfileDTO {
        profileID: string;
        label: string;
        mode: ExportRowMode;
        scope?: "DEFAULT" | "ORGANIZATION";
    }

    export type ExportProfile = ExportProfileDTO;

    export interface WorkedHoursCalculationProfileDTO {
        profileID: string;
        label: string;
        mode: WorkedHoursCalculationMode;
        queryMode: WorkedHoursCalculationQueryMode;
        scope: "DEFAULT" | "ORGANIZATION";
        description?: string;
        saturdayCutoffTime?: string;
        customQuerySQL?: string;
    }

    export type WorkedHoursCalculationProfile = WorkedHoursCalculationProfileDTO;

    export interface WorkedHoursCalculationConfigurationDTO {
        workedHoursCalculationProfileID: string;
        profile?: WorkedHoursCalculationProfileDTO;
    }

    export interface WorkedHoursCalculationConfiguration {
        workedHoursCalculationProfileID: string;
        profile?: WorkedHoursCalculationProfile;
    }

    export interface ColppyConfigurationDTO {
        organizationColppyCode?: string;
    }

    export type ColppyConfiguration = ColppyConfigurationDTO;

    export interface StatusMappingDayFlagsDTO {
        isVacation?: boolean;
        isAbsent?: boolean;
    }

    export interface StatusMappingGroupDTO {
        totalsField: string;
        label: string;
        builtIn?: boolean;
        dayFlags?: StatusMappingDayFlagsDTO;
        novedadAcronyms: string[];
    }

    export interface StatusMappingConfigurationDTO {
        groups: StatusMappingGroupDTO[];
    }

    export type StatusMappingGroup = StatusMappingGroupDTO;
    export type StatusMappingConfiguration = StatusMappingConfigurationDTO;

    export interface ExportRowDTO {
        rowID: string;
        processID: string;
        organizationID: string;
        employeeID: string;
        rowIndex: number;
        mode: ExportRowMode;
        fields: Record<string, unknown>;
        warnings: string[];
        errors: string[];
        source: Record<string, unknown>;
    }

    export interface ProcessEventDTO {
        eventID: string;
        processID: string;
        level: "INFO" | "WARNING" | "ERROR";
        step?: string;
        message: string;
        details?: unknown;
        timestamp?: number;
        firestoreCreationTimestamp?: number;
        firestoreUpdateTimestamp?: number;
    }

    export interface ProcessEmployeeDTO {
        employeeID: string;
        processID?: string;
        organizationID: string;
        displayName?: string;
        documentNumber?: string;
        payrollEmployeeID?: string;
        legajo?: string;
        name?: string;
        mobilePhone?: string;
        status: "PENDING" | "PROCESSED" | "FAILED" | "EXCLUDED";
        totals?: Record<string, number>;
        payrollConceptCount?: number;
        exportRowCount?: number;
        warningsCount?: number;
        errorsCount?: number;
        firestoreCreationTimestamp?: number;
        firestoreUpdateTimestamp?: number;
    }

    export interface ProcessRecord {
        processID: string;
        organizationID: string;
        title: string;
        dateFrom: string;
        dateTo: string;
        period?: string;
        employeeSelectionMode: EmployeeSelectionMode;
        selectedEmployeeIDs: string[];
        exportSystem?: string;
        calculationProfileID?: string;
        includeWorkedHours?: boolean;
        status: string;
        currentStep?: string;
        employeeCount: number;
        processedEmployeeCount: number;
        failedEmployeeCount: number;
        errorsCount: number;
        warningsCount: number;
        totals: Record<string, number>;
        outputFilePath?: string;
        outputFilename?: string;
        workedHoursCalculationProfileID?: string;
        workedHoursCalculationProfileSnapshot?: WorkedHoursCalculationProfileDTO;
        lastErrors: ProcessLastError[];
        lastErrorsCount: number;
        lastErrorsUpdatedAt?: number;
        firestoreCreationTimestamp?: number;
        firestoreUpdateTimestamp?: number;
    }

    export type ProcessLastError = ProcessLastErrorDTO;

    export interface ExportRow {
        rowID: string;
        processID: string;
        organizationID: string;
        employeeID: string;
        rowIndex: number;
        mode: ExportRowMode;
        fields: Record<string, unknown>;
        warnings: string[];
        errors: string[];
        source: Record<string, unknown>;
    }

    export interface ProcessEvent {
        eventID: string;
        processID: string;
        level: "INFO" | "WARNING" | "ERROR";
        step?: string;
        message: string;
        details?: unknown;
        timestamp?: number;
        firestoreCreationTimestamp?: number;
        firestoreUpdateTimestamp?: number;
    }

    export interface ProcessEmployee {
        employeeID: string;
        processID?: string;
        organizationID: string;
        displayName?: string;
        documentNumber?: string;
        payrollEmployeeID?: string;
        legajo?: string;
        name?: string;
        mobilePhone?: string;
        status: "PENDING" | "PROCESSED" | "FAILED" | "EXCLUDED";
        totals: Record<string, number>;
        payrollConceptCount: number;
        exportRowCount: number;
        warningsCount: number;
        errorsCount: number;
        firestoreCreationTimestamp?: number;
        firestoreUpdateTimestamp?: number;
    }

    export interface GenerateExportRowsRequest {
        profileID?: string;
    }

    export interface GenerateExportFileResultDTO {
        processID: string;
        organizationID: string;
        step: "GENERATE_FILE";
        queued: boolean;
    }

    export type GenerateExportFileResult = GenerateExportFileResultDTO;

    export class Parser {
        static parseProcessList(items: ProcessRecordDTO[]): ProcessRecord[] {
            return (items ?? []).map((item) => this.parseProcess(item));
        }

        static parseProcess(item: ProcessRecordDTO): ProcessRecord {
            const lastErrors = this.parseLastErrors(item.lastErrors);
            return {
                processID: item.processID,
                organizationID: item.organizationID,
                title: item.title,
                dateFrom: item.dateFrom,
                dateTo: item.dateTo,
                period: item.period,
                employeeSelectionMode: item.employeeSelectionMode,
                selectedEmployeeIDs: item.selectedEmployeeIDs ?? [],
                exportSystem: item.exportSystem,
                calculationProfileID: item.calculationProfileID,
                includeWorkedHours: item.includeWorkedHours,
                status: item.status,
                currentStep: item.currentStep,
                employeeCount: item.employeeCount ?? 0,
                processedEmployeeCount: item.processedEmployeeCount ?? 0,
                failedEmployeeCount: item.failedEmployeeCount ?? 0,
                errorsCount: item.errorsCount ?? 0,
                warningsCount: item.warningsCount ?? 0,
                totals: item.totals ?? {},
                outputFilePath: item.outputFilePath,
                outputFilename: item.outputFilename,
                workedHoursCalculationProfileID: item.workedHoursCalculationProfileID,
                workedHoursCalculationProfileSnapshot: item.workedHoursCalculationProfileSnapshot,
                lastErrors,
                lastErrorsCount: item.lastErrorsCount ?? lastErrors.length,
                lastErrorsUpdatedAt: item.lastErrorsUpdatedAt,
                firestoreCreationTimestamp: item.firestoreCreationTimestamp,
                firestoreUpdateTimestamp: item.firestoreUpdateTimestamp,
            };
        }

        static parseLastErrors(
            value: ProcessRecordDTO["lastErrors"],
        ): ProcessLastError[] {
            if (Array.isArray(value)) {
                return value;
            }
            if (!value || typeof value !== "object") {
                return [];
            }
            return Object.entries(value)
                .sort(([left], [right]) => Number(left) - Number(right))
                .map(([, error]) => error)
                .filter(Boolean);
        }

        static parseColppyConfiguration(item: ColppyConfigurationDTO | undefined): ColppyConfiguration {
            return {
                organizationColppyCode: item?.organizationColppyCode,
            };
        }

        static parseExportRowList(items: ExportRowDTO[]): ExportRow[] {
            return (items ?? []).map((item) => this.parseExportRow(item));
        }

        static parseExportRow(item: ExportRowDTO): ExportRow {
            return {
                rowID: item.rowID,
                processID: item.processID,
                organizationID: item.organizationID,
                employeeID: item.employeeID,
                rowIndex: item.rowIndex,
                mode: item.mode,
                fields: item.fields ?? {},
                warnings: item.warnings ?? [],
                errors: item.errors ?? [],
                source: item.source ?? {},
            };
        }

        static parseEventList(items: ProcessEventDTO[]): ProcessEvent[] {
            return (items ?? []).map((item) => this.parseEvent(item));
        }

        static parseEvent(item: ProcessEventDTO): ProcessEvent {
            return {
                eventID: item.eventID,
                processID: item.processID,
                level: item.level,
                step: item.step,
                message: item.message,
                details: item.details,
                timestamp: item.timestamp,
                firestoreCreationTimestamp: item.firestoreCreationTimestamp,
                firestoreUpdateTimestamp: item.firestoreUpdateTimestamp,
            };
        }

        static parseEmployeeList(items: ProcessEmployeeDTO[]): ProcessEmployee[] {
            return (items ?? []).map((item) => this.parseEmployee(item));
        }

        static parseEmployee(item: ProcessEmployeeDTO): ProcessEmployee {
            return {
                employeeID: item.employeeID,
                processID: item.processID,
                organizationID: item.organizationID,
                displayName: item.displayName,
                documentNumber: item.documentNumber,
                payrollEmployeeID: item.payrollEmployeeID,
                legajo: item.legajo,
                name: item.name,
                mobilePhone: item.mobilePhone,
                status: item.status,
                totals: item.totals ?? {},
                payrollConceptCount: item.payrollConceptCount ?? 0,
                exportRowCount: item.exportRowCount ?? 0,
                warningsCount: item.warningsCount ?? 0,
                errorsCount: item.errorsCount ?? 0,
                firestoreCreationTimestamp: item.firestoreCreationTimestamp,
                firestoreUpdateTimestamp: item.firestoreUpdateTimestamp,
            };
        }
    }
}

type ApiEnvelope<T> = {
    result?: T;
};

export class NovedadesExportService {
    private httpClient: HTTPClientInterface;

    constructor(httpClient: HTTPClientInterface) {
        this.httpClient = httpClient;
    }

    private addOrganizationHeader(request: HTTPRequest, organizationID: string) {
        request.headers = {
            ...(request.headers ?? {}),
            "x-organization-id": organizationID,
        };
    }

    private unwrapResponse<T>(response: ApiEnvelope<T> | T): T {
        if (
            response != null &&
            typeof response == "object" &&
            "result" in response
        ) {
            return (response as ApiEnvelope<T>).result as T;
        }

        return response as T;
    }

    async startProcess(
        payload: NovedadesExportServiceModel.StartProcessRequest
    ): Promise<NovedadesExportServiceModel.ProcessRecord> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.postProcess, {});
        request.method = "POST";
        request.body = payload;
        this.addOrganizationHeader(request, payload.organizationID);

        const response = await this.httpClient.execute(request);
        const process = this.unwrapResponse<NovedadesExportServiceModel.ProcessRecordDTO>(response);
        return NovedadesExportServiceModel.Parser.parseProcess(process);
    }

    async listProcesses(
        organizationID: string
    ): Promise<NovedadesExportServiceModel.ProcessRecord[]> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.getProcesses, {
            organization_id: organizationID,
        });
        request.method = "GET";
        this.addOrganizationHeader(request, organizationID);

        const response = await this.httpClient.execute(request);
        const processes = this.unwrapResponse<NovedadesExportServiceModel.ProcessRecordDTO[]>(response);
        return NovedadesExportServiceModel.Parser.parseProcessList(processes);
    }

    async listProfiles(
        organizationID: string
    ): Promise<NovedadesExportServiceModel.ExportProfile[]> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.getProfiles, {
            organization_id: organizationID,
        });
        request.method = "GET";
        this.addOrganizationHeader(request, organizationID);

        const response = await this.httpClient.execute(request);
        return this.unwrapResponse<NovedadesExportServiceModel.ExportProfileDTO[]>(response) ?? [];
    }

    async listWorkedHoursCalculationProfiles(
        organizationID: string
    ): Promise<NovedadesExportServiceModel.WorkedHoursCalculationProfile[]> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.getWorkedHoursCalculationProfiles, {
            organization_id: organizationID,
        });
        request.method = "GET";
        this.addOrganizationHeader(request, organizationID);

        const response = await this.httpClient.execute(request);
        return this.unwrapResponse<NovedadesExportServiceModel.WorkedHoursCalculationProfileDTO[]>(response) ?? [];
    }

    async getWorkedHoursCalculationConfiguration(
        organizationID: string
    ): Promise<NovedadesExportServiceModel.WorkedHoursCalculationConfiguration> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.getWorkedHoursCalculationConfiguration, {
            organization_id: organizationID,
        });
        request.method = "GET";
        this.addOrganizationHeader(request, organizationID);

        const response = await this.httpClient.execute(request);
        return this.unwrapResponse<NovedadesExportServiceModel.WorkedHoursCalculationConfigurationDTO>(response);
    }

    async updateWorkedHoursCalculationConfiguration(
        organizationID: string,
        workedHoursCalculationProfileID: string
    ): Promise<NovedadesExportServiceModel.WorkedHoursCalculationConfiguration> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.putWorkedHoursCalculationConfiguration, {
            organization_id: organizationID,
        });
        request.method = "PUT";
        request.body = { workedHoursCalculationProfileID };
        this.addOrganizationHeader(request, organizationID);

        const response = await this.httpClient.execute(request);
        return this.unwrapResponse<NovedadesExportServiceModel.WorkedHoursCalculationConfigurationDTO>(response);
    }

    async getColppyConfiguration(
        organizationID: string
    ): Promise<NovedadesExportServiceModel.ColppyConfiguration> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.getColppyConfiguration, {
            organization_id: organizationID,
        });
        request.method = "GET";
        this.addOrganizationHeader(request, organizationID);

        const response = await this.httpClient.execute(request);
        const configuration = this.unwrapResponse<NovedadesExportServiceModel.ColppyConfigurationDTO>(response);
        return NovedadesExportServiceModel.Parser.parseColppyConfiguration(configuration);
    }

    async updateColppyConfiguration(
        organizationID: string,
        organizationColppyCode: string
    ): Promise<NovedadesExportServiceModel.ColppyConfiguration> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.putColppyConfiguration, {
            organization_id: organizationID,
        });
        request.method = "PUT";
        request.body = { organizationColppyCode };
        this.addOrganizationHeader(request, organizationID);

        const response = await this.httpClient.execute(request);
        const configuration = this.unwrapResponse<NovedadesExportServiceModel.ColppyConfigurationDTO>(response);
        return NovedadesExportServiceModel.Parser.parseColppyConfiguration(configuration);
    }

    async getStatusMappingConfiguration(
        organizationID: string
    ): Promise<NovedadesExportServiceModel.StatusMappingConfiguration> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.getStatusMappingConfiguration, {
            organization_id: organizationID,
        });
        request.method = "GET";
        this.addOrganizationHeader(request, organizationID);

        const response = await this.httpClient.execute(request);
        return this.unwrapResponse<NovedadesExportServiceModel.StatusMappingConfigurationDTO>(response);
    }

    async updateStatusMappingConfiguration(
        organizationID: string,
        groups: NovedadesExportServiceModel.StatusMappingGroup[]
    ): Promise<NovedadesExportServiceModel.StatusMappingConfiguration> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.putStatusMappingConfiguration, {
            organization_id: organizationID,
        });
        request.method = "PUT";
        request.body = { groups };
        this.addOrganizationHeader(request, organizationID);

        const response = await this.httpClient.execute(request);
        return this.unwrapResponse<NovedadesExportServiceModel.StatusMappingConfigurationDTO>(response);
    }

    async getProcess(
        processID: string
    ): Promise<NovedadesExportServiceModel.ProcessRecord> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.getProcess, {
            process_id: processID,
        });
        request.method = "GET";

        const response = await this.httpClient.execute(request);
        const process = this.unwrapResponse<NovedadesExportServiceModel.ProcessRecordDTO>(response);
        return NovedadesExportServiceModel.Parser.parseProcess(process);
    }

    async restartProcess(
        processID: string
    ): Promise<NovedadesExportServiceModel.ProcessRecord> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.postRestartProcess, {
            process_id: processID,
        });
        request.method = "POST";

        const response = await this.httpClient.execute(request);
        const process = this.unwrapResponse<NovedadesExportServiceModel.ProcessRecordDTO>(response);
        return NovedadesExportServiceModel.Parser.parseProcess(process);
    }

    async generateExportRows(
        processID: string,
        payload: NovedadesExportServiceModel.GenerateExportRowsRequest = {}
    ): Promise<NovedadesExportServiceModel.ExportRow[]> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.postGenerateExportRows, {
            process_id: processID,
        });
        request.method = "POST";
        request.body = payload.profileID ? { profileID: payload.profileID } : {};

        const response = await this.httpClient.execute(request);
        const rows = this.unwrapResponse<NovedadesExportServiceModel.ExportRowDTO[]>(response);
        return NovedadesExportServiceModel.Parser.parseExportRowList(rows);
    }

    async generateExportFile(
        processID: string,
        payload: { exportSystem?: string; exportTipo?: string; includeWorkedHours?: boolean } = {}
    ): Promise<NovedadesExportServiceModel.GenerateExportFileResult> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.postGenerateExportFile, {
            process_id: processID,
        });
        request.method = "POST";
        request.body = {
            ...(payload.exportSystem ? { exportSystem: payload.exportSystem } : {}),
            ...(payload.exportTipo !== undefined ? { exportTipo: payload.exportTipo } : {}),
            ...(payload.includeWorkedHours !== undefined
                ? { includeWorkedHours: payload.includeWorkedHours }
                : {}),
        };

        const response = await this.httpClient.execute(request);
        return this.unwrapResponse<NovedadesExportServiceModel.GenerateExportFileResultDTO>(response);
    }

    async listExportRows(
        processID: string
    ): Promise<NovedadesExportServiceModel.ExportRow[]> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.getExportRows, {
            process_id: processID,
        });
        request.method = "GET";

        const response = await this.httpClient.execute(request);
        const rows = this.unwrapResponse<NovedadesExportServiceModel.ExportRowDTO[]>(response);
        return NovedadesExportServiceModel.Parser.parseExportRowList(rows);
    }

    async listEvents(
        processID: string
    ): Promise<NovedadesExportServiceModel.ProcessEvent[]> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.getEvents, {
            process_id: processID,
        });
        request.method = "GET";

        const response = await this.httpClient.execute(request);
        const events = this.unwrapResponse<NovedadesExportServiceModel.ProcessEventDTO[]>(response);
        return NovedadesExportServiceModel.Parser.parseEventList(events);
    }

    async listEmployees(
        processID: string
    ): Promise<NovedadesExportServiceModel.ProcessEmployee[]> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.NovedadesExport.getEmployees, {
            process_id: processID,
        });
        request.method = "GET";

        const response = await this.httpClient.execute(request);
        const employees = this.unwrapResponse<NovedadesExportServiceModel.ProcessEmployeeDTO[]>(response);
        return NovedadesExportServiceModel.Parser.parseEmployeeList(employees);
    }
}
